import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, ChevronDown, CheckCircle2, XCircle } from "lucide-react";
import { maskDateInput, dateBrToIso, isoToDateBr } from "@/lib/dateTimeMask";
import { PRESETS } from "@/lib/prazoPresets";

import { Dica } from "@/components/Dica";
// Calculadora de Prazos (Lote 42, regra corrigida no Lote 43) — especificação fechada com
// o Robson em chat separado (comparação com calculaprazo.com.br, decurso.com.br,
// prazolegal.com.br). Presets trabalhista (CLT) e cível (CPC) + modo genérico livre,
// feriados nacionais e recesso forense opcionais, detalhamento dia a dia (não só a data
// final). Lote 44: o formulário virou este componente à parte, reaproveitado tanto no
// modal (`PrazoCalculator.jsx`, aberto a partir de "Para validar") quanto na aba própria
// (`PrazoCalculatorPage.jsx`) — mesma lógica, dois lugares de entrada, como pedido.

const TIPOS = [
  { key: "trabalhista", label: "Trabalhista" },
  { key: "civel", label: "Cível" },
  { key: "generico", label: "Genérico" },
];

// Lista completa fechada com o Robson (22/09/2026) — cobre os recursos/atos mais comuns
// de cada esfera. base_legal é só texto de referência exibido, não muda o cálculo; o
// disclaimer no rodapé do resultado já deixa claro que é preciso conferir o caso concreto.


// Mapeia o case_area já usado no resto do app (trabalhista/civel/penal/outro) pro tipo
// da calculadora — penal/outro caem em "genérico", únicos tipos com preset por ora.
function caseAreaToTipo(caseArea) {
  if (caseArea === "trabalhista" || caseArea === "civel") return caseArea;
  return "generico";
}

// `initial` (opcional): { data_ato, case_area } — pré-preenchimento quando aberto a partir
// de um card "Conferir ata". `resetKey` muda toda vez que o chamador quer forçar reinício
// do formulário (ex.: modal reabrindo) — no uso em página cheia, fica undefined e o
// formulário só reinicia na primeira montagem.
export default function PrazoCalculatorForm({ initial, resetKey }) {
  const [tipo, setTipo] = useState(() => caseAreaToTipo(initial?.case_area));
  const [presetKey, setPresetKey] = useState("custom");
  const [dataAtoText, setDataAtoText] = useState(() => (initial?.data_ato ? isoToDateBr(initial.data_ato) : ""));
  const [dias, setDias] = useState("");
  const [regime, setRegime] = useState("uteis");
  const [excluirFeriados, setExcluirFeriados] = useState(true);
  const [excluirRecesso, setExcluirRecesso] = useState(false);
  const [baseLegal, setBaseLegal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Reinicia com o pré-preenchimento recebido (audiência de origem, se aberto a partir do
  // card "Conferir ata") sempre que `resetKey` muda — usado pelo modal, que quer um
  // formulário limpo toda vez que reabre. Na página cheia (sem resetKey), roda só uma vez.
  useEffect(() => {
    const t = caseAreaToTipo(initial?.case_area);
    setTipo(t);
    setPresetKey("custom");
    setDataAtoText(initial?.data_ato ? isoToDateBr(initial.data_ato) : "");
    setDias("");
    setRegime("uteis");
    setExcluirFeriados(true);
    setExcluirRecesso(false);
    setBaseLegal(null);
    setResult(null);
    setShowDetails(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const presets = PRESETS[tipo] || [];

  const applyPreset = (key) => {
    setPresetKey(key);
    if (key === "custom") {
      setBaseLegal(null);
      return;
    }
    const p = presets.find((x) => x.key === key);
    if (p) {
      setDias(String(p.dias));
      setRegime("uteis");
      setBaseLegal(p.base_legal);
    }
  };

  const changeTipo = (t) => {
    setTipo(t);
    setPresetKey("custom");
    setBaseLegal(null);
  };

  const calcular = async () => {
    const dataAto = dateBrToIso(dataAtoText);
    const n = parseInt(dias, 10);
    if (!dataAto) {
      toast.error("Preencha a data da disponibilização no formato dd/mm/aaaa");
      return;
    }
    if (!n || n <= 0) {
      toast.error("Informe o número de dias");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await http.post("/prazos/calcular", {
        data_ato: dataAto,
        dias: n,
        regime,
        excluir_feriados: excluirFeriados,
        excluir_recesso: excluirRecesso,
      });
      setResult(r.data);
      setShowDetails(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao calcular o prazo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs text-stone-500 mb-1.5 block">Tipo de prazo</Label>
        <div className="grid grid-cols-3 gap-2">
          {TIPOS.map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`prazo-calc-tipo-${t.key}`}
              onClick={() => changeTipo(t.key)}
              className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                tipo === t.key
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {presets.length > 0 && (
        <div>
          <Label className="text-xs text-stone-500 mb-1.5 block">Preset</Label>
          <Select value={presetKey} onValueChange={applyPreset}>
            <SelectTrigger data-testid="prazo-calc-preset-select">
              <SelectValue placeholder="Selecione um preset…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="custom">Personalizado</SelectItem>
              {presets.map((p) => (
                <SelectItem key={p.key} value={p.key}>{p.label} — {p.dias} dias úteis</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {baseLegal && (
            <p className="text-[11px] text-stone-400 mt-1">Base legal: {baseLegal}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-stone-500 mb-1.5 block">
            Data da disponibilização (DJEN) / ciência
          </Label>
          <Input
            data-testid="prazo-calc-data-ato"
            type="text"
            inputMode="numeric"
            placeholder="dd/mm/aaaa"
            maxLength={10}
            value={dataAtoText}
            onChange={(e) => setDataAtoText(maskDateInput(e.target.value))}
          />
        </div>
        <div>
          <Label className="text-xs text-stone-500 mb-1.5 block">Nº de dias</Label>
          <Input
            data-testid="prazo-calc-dias"
            type="number"
            min={1}
            value={dias}
            onChange={(e) => { setDias(e.target.value); setPresetKey("custom"); }}
          />
        </div>
      </div>

      <div>
        <Label className="text-xs text-stone-500 mb-1.5 block">Regime</Label>
        <div className="grid grid-cols-2 gap-2">
          {[{ key: "uteis", label: "Dias úteis" }, { key: "corridos", label: "Dias corridos" }].map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`prazo-calc-regime-${r.key}`}
              onClick={() => setRegime(r.key)}
              className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                regime === r.key
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-[11px] text-stone-400">
          {regime === "uteis"
            ? "Sábados e domingos sempre excluídos da contagem (regime de dias úteis)."
            : "Sábados e domingos contam normalmente (regime de dias corridos)."}
        </p>
        <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
          <Checkbox
            data-testid="prazo-calc-feriados"
            checked={excluirFeriados}
            onCheckedChange={(v) => setExcluirFeriados(Boolean(v))}
          />
          Excluir feriados nacionais
        </label>
        <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
          <Checkbox
            data-testid="prazo-calc-recesso"
            checked={excluirRecesso}
            onCheckedChange={(v) => setExcluirRecesso(Boolean(v))}
          />
          Excluir recesso forense (20/12 a 20/01)
        </label>
      </div>

      <Button
        data-testid="prazo-calc-submit"
        onClick={calcular}
        disabled={loading}
        className="w-full bg-emerald-brand text-white gap-2"
      >
        <Calculator className="w-4 h-4" strokeWidth={2} />
        {loading ? "Calculando…" : "Calcular"}
      </Button>

      {result && (
        <div className="border border-stone-200 rounded-md p-4 space-y-3 bg-stone-50" data-testid="prazo-calc-result">
          <div>
            <div className="text-[11px] font-bold tracking-widest uppercase text-stone-500 mb-1">
              Data final
            </div>
            <div className="font-display text-2xl font-bold text-stone-900" data-testid="prazo-calc-data-final">
              {isoToDateBr(result.data_final)} — {result.weekday_final}
            </div>
          </div>
          <p className="text-sm text-stone-500">{result.resumo}</p>

          <button
            type="button"
            data-testid="prazo-calc-toggle-details"
            onClick={() => setShowDetails((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-semibold text-emerald-brand"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showDetails ? "rotate-180" : ""}`} strokeWidth={2} />
            {showDetails ? "Ocultar detalhes" : "Ver detalhes"}
          </button>

          {showDetails && (
            <div className="space-y-1 max-h-56 overflow-y-auto pr-1" data-testid="prazo-calc-details">
              {result.detalhamento.map((d, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded ${
                    d.contado ? "bg-gold-100 text-gold-700" : "bg-stone-100 text-stone-400"
                  }`}
                >
                  <span className="flex items-center gap-1.5 tabular-nums">
                    {d.contado ? (
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                    )}
                    {isoToDateBr(d.date)} — {d.weekday}
                  </span>
                  <span>{d.contado ? "contado" : d.motivo}</span>
                </div>
              ))}
            </div>
          )}

          <Dica className="text-[11px] text-stone-400 pt-2 border-t border-stone-200">{`Observação: a contagem do prazo inicia-se 1 dia útil após a data da publicação (presumida 1 dia útil após a disponibilização). Os prazos sugeridos são meramente ilustrativos — necessária conferência do caso concreto (intimação pessoal, regras específicas do tribunal, etc.).`}</Dica>
        </div>
      )}
    </div>
  );
}
